import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRoom } from "./RoomProvider";

/**
 * TripTray — the VA's multi-trip attention rail.
 *
 * The room holds context; the VA holds attention. Pinned trips live here
 * as compact halo-cards. Click one → workspace switches to it. Close (×)
 * unpins. Currently active focus is highlighted.
 */
export default function TripTray() {
  const { pinnedTrips, findTrip, focus, focusOn, unpinTrip, sosTripId, tripRiskAlerts = [] } = useRoom();
  if (pinnedTrips.length === 0) return null;
  const focusedId = focus?.kind === "realTrip" ? focus.id : null;
  const riskTripIds = new Set(tripRiskAlerts.map((a) => a.trip_id));

  return (
    <div className="trip-tray" data-testid="trip-tray">
      <div className="tt-label">tray · press <kbd>P</kbd> to add</div>
      <div className="tt-cards">
        <AnimatePresence>
          {pinnedTrips.map(tid => {
            const t = findTrip(tid);
            if (!t) return null;
            const active = tid === focusedId;
            const isSos = tid === sosTripId;
            const atRisk = riskTripIds.has(tid);
            const s = String(t.status || "").toUpperCase();
            const color = isSos ? "#ef4444"
                        : atRisk ? "#f97316"
                        : ["ACCEPTED","EN_ROUTE","ARRIVED_PU","ON_TRIP","ARRIVED_DO"].includes(s) ? "#34d399"
                        : ["EXPIRED_OFFER","OFFER_EXHAUSTED","NO_SHOW_CONFIRMED"].includes(s) ? "#ef4444"
                        : "#f59e0b";
            return (
              <motion.div
                key={tid}
                className={`tt-card ${active ? "active" : ""} ${isSos ? "sos" : ""} ${atRisk ? "at-risk" : ""}`}
                style={{ "--halo": color }}
                initial={{ opacity: 0, y: 10, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.92 }}
                transition={{ duration: 0.25 }}
                onClick={() => focusOn("realTrip", tid)}
                data-testid={`tray-card-${tid}`}
              >
                <span className="tt-halo" />
                {atRisk && <span className="tt-risk-dot" title="Nova flagged this trip at risk" />}
                <div className="tt-body">
                  <div className="tt-id">{String(tid).slice(0, 8)}</div>
                  <div className="tt-rider">{t.rider?.name || "rider"}</div>
                  <div className="tt-meta">{s.toLowerCase().replace(/_/g, " ")}</div>
                </div>
                <button className="tt-close"
                        title="unpin"
                        onClick={(e) => { e.stopPropagation(); unpinTrip(tid); }}>×</button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
